type HasPrice = { price: number; cost_price: number };

export const priceToCents = (price: number): number => Math.round(price * 100);

export const hasDisplayablePrice = (price: number): boolean =>
  Number.isFinite(price) && priceToCents(price) >= 1;

export const hasSellablePrice = <T extends HasPrice>(product: T): boolean =>
  hasDisplayablePrice(product.price) && hasDisplayablePrice(product.cost_price);

export const filteredProductLog = <
  T extends { id: number; sku: string; price: number },
>(
  products: T[],
) =>
  products.map((product) => ({
    id: product.id,
    sku: product.sku,
    price: product.price,
    cents: priceToCents(product.price),
  }));

// Tier-aware pricing.
//
// Two price values are derived from the product:
//   A = price             — retail. Guests see only this; associates and
//                           affiliates see it too and associates PAY it.
//   B = cost_price * 1.15  — partner price (cost + 15% service fee). Shown
//                           to logged-in users; only affiliates PAY it.
//
// Display vs. charge: associates and affiliates both SEE both prices, but
// associates are charged A (retail) and affiliates are charged B (partner).
// Guests see only A and cannot check out.
//
// Products with price or cost_price === 0 are filtered upstream by
// hasSellablePrice (above), so both A and B are guaranteed positive at
// render/charge time.

export type Tier = "guest" | "associate" | "affiliate";

type ProductPriceFields = {
  price: number;
  cost_price: number;
};

// Both prices round UP to the nearest whole dollar (Math.ceil). Rounding
// lives here, the single source of truth, so the displayed price (PriceTag,
// cart) and the amount charged at checkout (computeCheckoutPrice → Stripe
// line items) are always the same whole-dollar figure.
export const computePriceA = (product: ProductPriceFields): number =>
  Math.ceil(product.price);

export const computePriceB = (product: ProductPriceFields): number =>
  Math.ceil(product.cost_price * 1.15);

// Resolve the effective tier from auth + subscription state. Defaults
// to "associate" for any authenticated user whose subscription tier is
// unknown / missing, so a corrupted subscription record never silently
// gives an affiliate discount.
export const normalizeTier = (input: {
  session: unknown;
  subscription: { tier?: string | null } | null | undefined;
}): Tier => {
  if (!input.session) {
    return "guest";
  }
  const raw = input.subscription?.tier?.toLowerCase().trim();
  if (raw === "affiliate") {
    return "affiliate";
  }
  return "associate";
};

// The price charged at checkout. Affiliates pay priceB (partner, cost ×
// 1.15); associates pay priceA (retail). Guests cannot check out
// (purchaseGhostItems is a protected procedure), so asking for a guest
// checkout price is a programmer error.
export const computeCheckoutPrice = (
  tier: Tier,
  product: ProductPriceFields,
): number => {
  switch (tier) {
    case "affiliate":
      return computePriceB(product);
    case "associate":
      return computePriceA(product);
    case "guest":
      throw new Error("Guests cannot check out; no checkout price available.");
  }
};
